import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import type {
  PageResponse,
  SprintSummary,
  WorkItemSummary,
} from "../../shared/types/api";
import { WorkItemTypeBadge } from "../workitems/WorkItemTypeBadge";
import { formatHours } from "../workitems/workItemVocabulary";

/**
 * Pulls work from the backlog into a sprint, without leaving the sprint.
 *
 * <p>Adding work used to mean going to the backlog, finding the items among everything else in
 * the project, and picking the sprint back out of a dropdown — planning from the wrong end. The
 * question at planning time is "what else goes in this sprint", and it is asked while looking at
 * what is already in it.
 *
 * <p>The running total is the point of the footer. A sprint is planned against capacity, and a
 * panel that let somebody tick eleven items and only showed the damage after saving would be a
 * planning tool that hides the one number planning is about.
 */
export function AddToSprintDrawer({
  sprint,
  projectId,
  onClose,
  onSaved,
}: {
  sprint: SprintSummary | null;
  projectId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const backlog = useQuery({
    queryKey: ["backlog", projectId, ""],
    queryFn: () =>
      api.get<PageResponse<WorkItemSummary>>(
        `/api/v1/backlog?projectId=${projectId}&size=200`,
      ),
    enabled: Boolean(sprint) && Boolean(projectId),
  });

  useEffect(() => {
    if (sprint) {
      setChosen(new Set());
      setQuery("");
      setError(null);
    }
  }, [sprint]);

  const all = backlog.data?.content ?? [];
  const needle = query.trim().toLowerCase();
  const items = needle
    ? all.filter(
        (item) =>
          item.title.toLowerCase().includes(needle) ||
          item.itemKey.toLowerCase().includes(needle),
      )
    : all;

  const chosenItems = all.filter((item) => chosen.has(item.id));
  const chosenPoints = chosenItems.reduce(
    (total, item) => total + (item.storyPoints ?? 0),
    0,
  );
  const chosenHours = chosenItems.reduce(
    (total, item) => total + (item.currentEstimateHours ?? 0),
    0,
  );

  const toggle = (id: string) => {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const submit = async () => {
    if (!sprint || chosen.size === 0) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/v1/backlog/move-to-sprint", {
        workItemIds: [...chosen],
        sprintId: sprint.id,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not add that work.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(sprint)}
      onClose={onClose}
      title="Add work to this sprint"
      subtitle={sprint?.name}
      width="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-500">
            {chosen.size === 0
              ? "Nothing selected yet."
              : `${chosen.size} selected · ${chosenPoints} pts · ${formatHours(chosenHours)}`}
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              loading={saving}
              disabled={chosen.size === 0}
            >
              {saving ? "Adding…" : `Add ${chosen.size || ""}`.trim()}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {sprint?.status === "ACTIVE" && (
          <InlineAlert tone="warning">
            This sprint is already running, so anything added now counts as
            unplanned work rather than part of what the team committed to. That
            is exactly how it will read on the report — which is the point.
          </InlineAlert>
        )}

        <Field label="Search">
          <Input
            placeholder="Title or key"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </Field>

        {backlog.isPending && <LoadingState label="Loading the backlog…" />}
        {backlog.isError && (
          <ErrorState
            error={backlog.error}
            onRetry={() => void backlog.refetch()}
          />
        )}

        {backlog.isSuccess &&
          (items.length === 0 ? (
            <EmptyState
              title={
                query ? "Nothing matches that search" : "The backlog is empty"
              }
              message={
                query
                  ? "Try a broader search — the work may be in a sprint already."
                  : "Everything in this project is either in a sprint or finished."
              }
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {items.map((item) => (
                <li key={item.id}>
                  <label className="flex cursor-pointer items-center gap-3 py-2">
                    <input
                      type="checkbox"
                      checked={chosen.has(item.id)}
                      onChange={() => toggle(item.id)}
                    />
                    <WorkItemTypeBadge type={item.workItemType} />
                    <span className="font-mono text-xs text-ink-500">
                      {item.itemKey}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-ink-900">
                      {item.title}
                    </span>
                    {/* Carried work is not new work, and planning it again blind is how the
                        same failure gets re-planned. */}
                    {item.carriedOverFromSprintName && (
                      <StatusBadge tone="amber">
                        Undone from {item.carriedOverFromSprintName}
                      </StatusBadge>
                    )}
                    <span className="tnum shrink-0 text-xs text-ink-500">
                      {item.storyPoints != null
                        ? `${item.storyPoints} pts`
                        : "unsized"}
                    </span>
                    <span className="tnum w-14 shrink-0 text-right text-xs text-ink-500">
                      {formatHours(item.currentEstimateHours)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          ))}
      </div>
    </Drawer>
  );
}
